import { useCallback, useEffect, useState } from "react";

import { usePromise } from "react-use";

import { useCart } from "./useCart";
import { useDefinition } from "./useDefinition";
import { useMessage } from "./useMessage";

import { useClient } from "./useClient";
import { useErrorHandling } from "./useErrorHandling";

export function usePrice({
	busyBlocker,
}: {
	busyBlocker?: eXpress.BalancedScope;
} = {}) {
	const client = useClient();

	const ifMounted = usePromise();

	const { createErrorHandler } = useErrorHandling();
	const cart = useCart();
	const definition = useDefinition();
	const [price, setPrice] = useState<undefined | eXpress.core.IPrice>(undefined);

	const getPrice = useCallback(() => {
		if (!definition || !cart) {
			setPrice(undefined);
			return;
		}

		const productVariant = definition.getSelectedProductVariant();

		const variableAmount = definition.getUsedPages(
			(page) => page.spread.kind !== eXpress.core.SpreadKind.Cover,
		).length;

		const productOptionCode = definition.selectedProductOptionCode;

		const allowedAttributeCodes = definition.product.attributes
			? definition.product.attributes.keys
			: undefined;

		const promise = client.getPriceAsync(
			cart,
			productVariant.code,
			variableAmount,
			productOptionCode,
			allowedAttributeCodes,
		);

		ifMounted(busyBlocker ? busyBlocker.while(promise) : promise)
			.then(setPrice)
			.catch((error: unknown) => {
				setPrice(undefined);
				createErrorHandler()(error);
			});
	}, [busyBlocker, cart, client, ifMounted, createErrorHandler, definition]);

	useEffect(() => getPrice(), [getPrice]);

	useMessage((message) => {
		if (eXpress.core.shouldUpdatePrice(message)) {
			getPrice();
		}
	});

	return price;
}
